import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ShieldCheck,
  ShieldAlert,
  CheckCircle2,
  XCircle,
  User,
  RefreshCw,
  FileCheck2,
  FileText,
  Award,
  IdCard,
  Receipt,
  Lock,
} from "lucide-react";
import { server } from "../server";

const VerifyTransferCertificate = () => {
  const { uniqueId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tcData, setTcData] = useState(null);
  const [isVerified, setIsVerified] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [verifiedAt, setVerifiedAt] = useState("");

  const fetchTcData = async (idToFetch) => {
    if (!idToFetch) {
      setLoading(false);
      setTcData(null);
      setIsVerified(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${server}/transferCertificates/verify/${idToFetch}`,
        {
          headers: {
            accept: "*/*",
          },
        },
      );

      if (!response.ok) {
        if (response.status === 404) {
          setIsVerified(false);
          setTcData(null);
          setError("Transfer Certificate record not found in school registry.");
        } else {
          throw new Error(`Server responded with status ${response.status}`);
        }
      } else {
        const result = await response.json();
        if (result.success && result.verified && result.data) {
          setTcData(result.data);
          setIsVerified(true);
          setVerifiedAt(
            new Date().toLocaleString("en-IN", {
              dateStyle: "medium",
              timeStyle: "short",
            }),
          );
        } else {
          setIsVerified(false);
          setTcData(result.data || null);
          setError(
            "This Transfer Certificate could not be verified in the database.",
          );
        }
      }
    } catch (err) {
      console.error("TC Verification error:", err);
      setError(
        "Unable to connect to verification server. Please try again later.",
      );
      setIsVerified(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (uniqueId) {
      setSearchInput(uniqueId);
      fetchTcData(uniqueId);
    } else {
      setLoading(false);
    }
  }, [uniqueId]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchInput.trim()) {
      navigate(`/verify/transferCertificate/${searchInput.trim()}`);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      return new Date(dateStr).toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    } catch (e) {
      return dateStr;
    }
  };

  const student = tcData?.student || {};

  return (
    <div className="min-h-screen bg-slate-50 text-slate-800 font-sans pb-10 pt-4 px-3 sm:px-6">
      <div className="max-w-3xl mx-auto space-y-4">
        {/* Portal Header & Service Switcher */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="flex items-center space-x-3 w-full md:w-auto">
            <div className="w-10 h-10 rounded-lg bg-blue-50 text-primary border border-blue-200 flex items-center justify-center shrink-0">
              <FileCheck2 className="w-6 h-6 text-primary" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-base sm:text-lg font-bold text-slate-900">
                  Dee Novo Verification Portal
                </h1>
                <span className="bg-emerald-50 text-emerald-700 text-[10px] px-2 py-0.5 rounded-full font-bold uppercase border border-emerald-200">
                  Official
                </span>
              </div>
              <p className="text-xs text-slate-500">
                Transfer Certificate Public Registry
              </p>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="flex items-center bg-slate-100 p-1 rounded-lg border border-slate-200 text-xs font-semibold w-full md:w-auto justify-center">
            <Link
              to={uniqueId ? `/verify/idCard/${uniqueId}` : "/verify/idCard"}
              className="px-3 py-1.5 rounded-md text-slate-600 hover:text-primary flex items-center gap-1.5 transition-colors"
            >
              <IdCard className="w-3.5 h-3.5" /> ID Card
            </Link>
            <Link
              to={
                uniqueId
                  ? `/verify/transferCertificate/${uniqueId}`
                  : "/verify/transferCertificate"
              }
              className="px-3 py-1.5 rounded-md bg-white text-primary shadow-xs font-bold flex items-center gap-1.5 transition-colors border border-slate-200"
            >
              <FileCheck2 className="w-3.5 h-3.5" /> Transfer Cert
            </Link>
            <Link
              to={
                uniqueId
                  ? `/verify/studentFee/${uniqueId}`
                  : "/verify/studentFee"
              }
              className="px-3 py-1.5 rounded-md text-slate-600 hover:text-primary flex items-center gap-1.5 transition-colors"
            >
              <Receipt className="w-3.5 h-3.5" /> Fee Receipt
            </Link>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-xl p-8 text-center shadow-sm border border-slate-200 space-y-3">
            <RefreshCw className="w-8 h-8 text-primary animate-spin mx-auto" />
            <h3 className="text-sm font-bold text-slate-800">
              Verifying Transfer Certificate...
            </h3>
            <p className="text-xs text-slate-500 font-mono">{uniqueId}</p>
          </div>
        )}

        {/* Empty State */}
        {!loading && !uniqueId && (
          <div className="bg-white rounded-xl p-8 text-center shadow-sm border border-slate-200 space-y-3">
            <FileText className="w-10 h-10 text-primary mx-auto" />
            <h2 className="text-base font-bold text-slate-900">
              Transfer Certificate Verification
            </h2>
            <p className="text-xs text-slate-600 max-w-sm mx-auto">
              Please enter the unique certificate code below to check official
              registry details.
            </p>
            <form
              onSubmit={handleSearchSubmit}
              className="max-w-md mx-auto flex items-center gap-2 pt-2"
            >
              <input
                type="text"
                placeholder="Enter TC Code (UUID)..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="flex-1 px-3.5 py-2 bg-slate-50 border border-slate-300 rounded-lg text-xs font-mono focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <button
                type="submit"
                className="px-4 py-2 bg-primary text-white font-bold text-xs rounded-lg hover:bg-primaryDark transition-colors"
              >
                Verify
              </button>
            </form>
          </div>
        )}

        {/* Light Error State */}
        {!loading && uniqueId && (!isVerified || error) && (
          <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-red-200">
            <div className="bg-red-50 text-red-800 px-4 py-3 flex items-center space-x-2 border-b border-red-200">
              <ShieldAlert className="w-5 h-5 text-red-600 shrink-0" />
              <h2 className="text-sm font-bold">TC VERIFICATION FAILED</h2>
            </div>
            <div className="p-6 text-center space-y-3">
              <XCircle className="w-10 h-10 text-red-500 mx-auto" />
              <h3 className="text-base font-bold text-slate-900">
                {error || "Transfer Certificate Not Found"}
              </h3>
              <p className="text-xs text-slate-600 font-mono">{uniqueId}</p>
              <button
                onClick={() => fetchTcData(uniqueId)}
                className="px-4 py-1.5 bg-slate-100 text-slate-700 text-xs font-bold rounded-lg hover:bg-slate-200 border border-slate-300 inline-flex items-center gap-1.5"
              >
                <RefreshCw className="w-3.5 h-3.5" /> Retry
              </button>
            </div>
          </div>
        )}

        {/* Light Verified Success State */}
        {!loading && isVerified && tcData && (
          <div className="space-y-4">
            {/* Light Emerald Verified Banner */}
            <div className="bg-emerald-50 text-emerald-900 rounded-xl p-4 shadow-sm border border-emerald-200 flex items-center space-x-3">
              <div className="w-9 h-9 bg-emerald-100 text-emerald-700 rounded-lg flex items-center justify-center shrink-0 border border-emerald-200">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-sm sm:text-base font-bold text-emerald-950">
                    VERIFIED TRANSFER CERTIFICATE
                  </h2>
                  <span className="bg-emerald-200 text-emerald-800 text-[10px] px-2 py-0.5 rounded font-bold">
                    GENUINE
                  </span>
                </div>
                <p className="text-xs text-emerald-700">
                  TC No:{" "}
                  <strong className="font-mono text-primary font-bold">
                    {tcData.tcNumber}
                  </strong>{" "}
                  • Official Record
                </p>
              </div>
            </div>

            {/* Official TC Document Card */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              {/* Light Blue Header Ribbon */}
              <div className="bg-blue-50/90 text-slate-900 p-4 sm:p-5 border-b border-blue-200">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <img
                      src="/assets/images/logo.png"
                      alt="Logo"
                      className="h-12 w-auto bg-white p-1 rounded-lg object-contain border border-blue-200 shadow-xs"
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                    <div>
                      <h2 className="text-base sm:text-lg font-extrabold uppercase text-primary tracking-wide">
                        DEE NOVO SCHOOL
                      </h2>
                      <p className="text-xs text-slate-600">
                        Affiliated to CBSE • Dumka, Jharkhand
                      </p>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <span className="text-[10px] uppercase text-slate-500 font-semibold block">
                      TC Serial No
                    </span>
                    <span className="text-xs sm:text-sm font-mono font-bold text-primary">
                      {tcData.tcNumber}
                    </span>
                  </div>
                </div>
              </div>

              {/* Main Content Area */}
              <div className="p-4 sm:p-5 space-y-4">
                {/* Dates Bar */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 bg-slate-50 p-3 rounded-lg border border-slate-200 text-xs">
                  <div>
                    <span className="text-[10px] text-slate-400 font-semibold uppercase block">
                      TC Serial Number
                    </span>
                    <p className="font-mono font-bold text-slate-800">
                      {tcData.tcNumber}
                    </p>
                  </div>
                  <div>
                    <span className="text-[10px] text-slate-400 font-semibold uppercase block">
                      Issue Date
                    </span>
                    <p className="font-bold text-slate-800">
                      {formatDate(tcData.issueDate)}
                    </p>
                  </div>
                  <div>
                    <span className="text-[10px] text-slate-400 font-semibold uppercase block">
                      Application Date
                    </span>
                    <p className="font-semibold text-slate-700">
                      {formatDate(tcData.applicationDate)}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 items-start">
                  {/* Photo Column */}
                  <div className="flex flex-col items-center text-center space-y-2">
                    <div className="w-28 h-36 rounded-lg overflow-hidden shadow-xs border border-slate-300 bg-slate-100">
                      {student.studentPhoto ? (
                        <img
                          src={student.studentPhoto}
                          alt={student.studentName || "Student"}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center bg-slate-200 text-slate-400">
                          <User className="w-10 h-10" />
                          <span className="text-[10px] mt-1">No Photo</span>
                        </div>
                      )}
                    </div>

                    <div className="text-center pt-1">
                      <span className="text-[10px] text-slate-400 font-mono block">
                        Student UUID
                      </span>
                      <span className="text-[11px] font-mono font-semibold text-slate-700 bg-slate-100 px-2 py-0.5 rounded inline-block border border-slate-200 break-all max-w-[160px]">
                        {student.uniqueId || uniqueId}
                      </span>
                    </div>
                  </div>

                  {/* Student Details Grid */}
                  <div className="sm:col-span-3 space-y-3">
                    <div>
                      <span className="text-[11px] font-bold text-primary bg-blue-50 px-2.5 py-0.5 rounded border border-blue-200">
                        Admission No: {student.admissionNumber || "N/A"}
                      </span>
                      <h3 className="text-xl font-bold text-slate-900 mt-1">
                        {student.studentName || "N/A"}
                      </h3>
                      <p className="text-xs text-slate-500 font-medium">
                        System Student ID: #
                        {student.studentId || tcData.studentId}
                      </p>
                    </div>

                    {/* Aligned Attributes */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 bg-slate-50 p-3 rounded-lg border border-slate-200 text-xs">
                      <div>
                        <span className="text-[10px] font-semibold text-slate-400 uppercase block">
                          Class
                        </span>
                        <p className="font-bold text-slate-800">
                          {student.class || "N/A"}
                        </p>
                      </div>
                      <div>
                        <span className="text-[10px] font-semibold text-slate-400 uppercase block">
                          Section
                        </span>
                        <p className="font-bold text-slate-800">
                          {student.section || "N/A"}
                        </p>
                      </div>
                      <div>
                        <span className="text-[10px] font-semibold text-slate-400 uppercase block">
                          Roll Number
                        </span>
                        <p className="font-bold text-slate-800">
                          {student.rollNumber || "N/A"}
                        </p>
                      </div>
                      <div>
                        <span className="text-[10px] font-semibold text-slate-400 uppercase block">
                          Gender
                        </span>
                        <p className="font-bold text-slate-800">
                          {student.gender || "N/A"}
                        </p>
                      </div>
                      <div>
                        <span className="text-[10px] font-semibold text-slate-400 uppercase block">
                          Date of Birth
                        </span>
                        <p className="font-bold text-slate-800">
                          {formatDate(student.dateOfBirth)}
                        </p>
                      </div>
                      <div>
                        <span className="text-[10px] font-semibold text-slate-400 uppercase block">
                          Session
                        </span>
                        <p className="font-bold text-slate-800">
                          {student.session || "N/A"}
                        </p>
                      </div>
                    </div>

                    {/* Parents Info */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                      <div className="p-2.5 bg-white rounded-lg border border-slate-200">
                        <span className="text-[10px] text-slate-400 font-semibold uppercase block">
                          Father's Name
                        </span>
                        <p className="font-bold text-slate-800">
                          {student.fatherName || "N/A"}
                        </p>
                      </div>
                      <div className="p-2.5 bg-white rounded-lg border border-slate-200">
                        <span className="text-[10px] text-slate-400 font-semibold uppercase block">
                          Mother's Name
                        </span>
                        <p className="font-bold text-slate-800">
                          {student.motherName || "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Particulars & Conduct */}
                <div className="space-y-2 pt-2 border-t border-slate-200 text-xs">
                  <h4 className="font-bold text-slate-900 flex items-center gap-1.5">
                    <Award className="w-4 h-4 text-primary" /> Certificate
                    Particulars & Conduct
                  </h4>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <div className="p-2.5 bg-slate-50 rounded-lg border border-slate-200">
                      <span className="text-[10px] font-semibold text-slate-400 uppercase block">
                        Reason for Leaving
                      </span>
                      <p className="font-bold text-slate-800">
                        {tcData.reasonForLeaving || "N/A"}
                      </p>
                    </div>

                    <div className="p-2.5 bg-emerald-50 rounded-lg border border-emerald-200">
                      <span className="text-[10px] font-semibold text-emerald-700 uppercase block">
                        General Conduct
                      </span>
                      <p className="font-bold text-emerald-900 flex items-center gap-1">
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />{" "}
                        {tcData.conduct || "Good"}
                      </p>
                    </div>

                    <div className="p-2.5 bg-slate-50 rounded-lg border border-slate-200">
                      <span className="text-[10px] font-semibold text-slate-400 uppercase block">
                        Official Remarks
                      </span>
                      <p className="font-semibold text-slate-700">
                        {tcData.remarks || "None"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Footer Stamp */}
                <div className="pt-3 border-t border-slate-200 flex items-center justify-between text-[11px] text-slate-500">
                  <div className="flex items-center space-x-1.5">
                    <ShieldCheck className="w-4 h-4 text-emerald-600" />
                    <span>
                      Verified via Dee Novo Live Registry •{" "}
                      {verifiedAt || "Just now"}
                    </span>
                  </div>
                  {tcData.createdByUser && (
                    <div className="text-right">
                      <span>
                        Issued By:{" "}
                        <strong className="text-slate-700">
                          {tcData.createdByUser.fullName}
                        </strong>
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Light Disclaimer */}
            <div className="bg-blue-50 text-blue-900 rounded-lg p-3 text-xs flex items-start space-x-2 border border-blue-200">
              <Lock className="w-4 h-4 text-primary shrink-0 mt-0.5" />
              <div>
                <span className="font-bold text-blue-950">
                  Verification Note:
                </span>{" "}
                Transfer Certificate{" "}
                <strong className="font-mono text-primary">
                  {tcData.tcNumber}
                </strong>{" "}
                is officially verified in Dee Novo School database.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VerifyTransferCertificate;
